import {
  LETTER_TEMPLATE_PLACEHOLDER_KEYS,
} from '../adminProspectLetterTemplateDisplay.js';
import {
  LETTER_TEMPLATE_FONT_SIZES,
} from './LetterFontSizeExtension.js';

export const LETTER_TEMPLATE_ALLOWED_NODE_TYPES = new Set([
  'doc',
  'paragraph',
  'heading',
  'text',
  'hardBreak',
  'bulletList',
  'orderedList',
  'listItem',
  'blockquote',
  'horizontalRule',
  'letterPlaceholder',
  'letterQrBlock',
  'letterImage',
]);

export const LETTER_TEMPLATE_ALLOWED_MARKS = new Set(['bold', 'italic', 'strike', 'letterFont', 'letterFontSize']);

export const LETTER_TEMPLATE_ALLOWED_FONT_FAMILIES = new Set(['helvetica', 'times', 'courier']);

export const LETTER_TEMPLATE_ALLOWED_ALIGNMENTS = new Set(['left', 'center', 'right']);

export const LETTER_TEMPLATE_MAX_QR_BLOCKS = 1;

export const LETTER_TEMPLATE_MAX_IMAGE_NODES = 6;

/** Native Tiptap may omit content on empty paragraph/heading. */
const OPTIONAL_EMPTY_CONTENT_CONTAINER_TYPES = new Set(['paragraph', 'heading']);

const STRUCTURAL_CONTAINER_TYPES = new Set([
  'doc',
  'bulletList',
  'orderedList',
  'listItem',
  'blockquote',
]);

const PARENT_ALLOWED_CHILDREN = {
  doc: new Set([
    'paragraph',
    'heading',
    'bulletList',
    'orderedList',
    'blockquote',
    'horizontalRule',
    'letterQrBlock',
    'letterImage',
  ]),
  paragraph: new Set(['text', 'hardBreak', 'letterPlaceholder']),
  heading: new Set(['text', 'hardBreak', 'letterPlaceholder']),
  bulletList: new Set(['listItem']),
  orderedList: new Set(['listItem']),
  listItem: new Set(['paragraph', 'bulletList', 'orderedList', 'blockquote', 'horizontalRule']),
  blockquote: new Set(['paragraph', 'heading', 'bulletList', 'orderedList', 'horizontalRule']),
};

/**
 * @param {unknown} document
 * @returns {{ valid: boolean, issues: string[] }}
 */
export function validateLetterTemplateDocument(document) {
  const issues = [];
  if (!document || typeof document !== 'object' || Array.isArray(document)) {
    return { valid: false, issues: ['invalid_root'] };
  }
  if (document.type !== 'doc') {
    return { valid: false, issues: ['invalid_root_type'] };
  }
  if (!Array.isArray(document.content)) {
    issues.push('doc_missing_content');
    return { valid: false, issues };
  }

  let qrCount = 0;
  let imageCount = 0;
  walkNode(document, 'doc', (node, parentType) => {
    const type = node?.type;
    if (!LETTER_TEMPLATE_ALLOWED_NODE_TYPES.has(type)) {
      issues.push(`unsupported_node:${type}`);
      return;
    }
    if (type === 'letterQrBlock') {
      qrCount += 1;
      if (parentType !== 'doc') {
        issues.push('qr_not_top_level');
      }
      const align = node?.attrs?.align;
      if (align != null && !LETTER_TEMPLATE_ALLOWED_ALIGNMENTS.has(align)) {
        issues.push(`invalid_qr_align:${align}`);
      }
    }
    if (type === 'letterImage') {
      imageCount += 1;
      if (parentType !== 'doc') {
        issues.push('image_not_top_level');
      }
      const attrs = node?.attrs || {};
      if (typeof attrs.assetId !== 'string' || !attrs.assetId.trim()) {
        issues.push('invalid_image_asset');
      }
      const widthPct = Number(attrs.widthPct);
      if (!Number.isInteger(widthPct) || widthPct < 10 || widthPct > 100) {
        issues.push('invalid_image_width');
      }
      const align = attrs.align;
      if (align != null && !LETTER_TEMPLATE_ALLOWED_ALIGNMENTS.has(align)) {
        issues.push('invalid_image_align');
      }
      if (attrs.alt != null && typeof attrs.alt !== 'string') {
        issues.push('invalid_image_alt');
      }
      if (typeof attrs.alt === 'string' && attrs.alt.length > 200) {
        issues.push('invalid_image_alt');
      }
      if (attrs.url || attrs.signedUrl || attrs.downloadUrl || attrs.storagePath || attrs.base64) {
        issues.push('invalid_image_url_in_canonical');
      }
    }
    if (type === 'letterPlaceholder') {
      const key = node?.attrs?.key;
      if (!LETTER_TEMPLATE_PLACEHOLDER_KEYS.includes(key)) {
        issues.push(`unknown_placeholder:${key || 'missing'}`);
      }
      if (!['paragraph', 'heading'].includes(parentType)) {
        issues.push('placeholder_invalid_parent');
      }
      validateMarks(node?.marks, issues);
    }
    if (type === 'text') {
      validateMarks(node?.marks, issues);
    }
    if (type === 'paragraph' || type === 'heading') {
      const align = node?.attrs?.align;
      if (align != null && !LETTER_TEMPLATE_ALLOWED_ALIGNMENTS.has(align)) {
        issues.push(`invalid_align:${align}`);
      }
      if (type === 'heading') {
        const level = node?.attrs?.level;
        if (![1, 2, 3].includes(level)) {
          issues.push(`invalid_heading_level:${level}`);
        }
      }
      if (node.content != null && !Array.isArray(node.content)) {
        issues.push(`invalid_content:${type}`);
      }
    }
    if (STRUCTURAL_CONTAINER_TYPES.has(type) && !Array.isArray(node.content)) {
      issues.push(`missing_content:${type}`);
    }
    const allowedChildren = PARENT_ALLOWED_CHILDREN[type];
    const childContent = Array.isArray(node.content)
      ? node.content
      : OPTIONAL_EMPTY_CONTENT_CONTAINER_TYPES.has(type)
        ? []
        : null;
    if (allowedChildren && Array.isArray(childContent)) {
      for (const child of childContent) {
        if (!allowedChildren.has(child?.type)) {
          issues.push(`invalid_child:${type}->${child?.type}`);
        }
      }
    }
  });

  if (qrCount > LETTER_TEMPLATE_MAX_QR_BLOCKS) {
    issues.push('too_many_qr_blocks');
  }
  if (imageCount > LETTER_TEMPLATE_MAX_IMAGE_NODES) {
    issues.push('too_many_images');
  }

  return { valid: issues.length === 0, issues };
}

/**
 * @param {unknown} marks
 * @param {string[]} issues
 */
function validateMarks(marks, issues) {
  if (!Array.isArray(marks)) return;
  for (const mark of marks) {
    if (!LETTER_TEMPLATE_ALLOWED_MARKS.has(mark?.type)) {
      issues.push(`unsupported_mark:${mark?.type}`);
    }
    if (mark?.type === 'letterFont') {
      const family = mark?.attrs?.family;
      if (!LETTER_TEMPLATE_ALLOWED_FONT_FAMILIES.has(family)) {
        issues.push(`invalid_font:${family || 'missing'}`);
      }
    }
    if (mark?.type === 'letterFontSize') {
      const pt = Number(mark?.attrs?.pt);
      if (!LETTER_TEMPLATE_FONT_SIZES.includes(pt)) {
        issues.push(`invalid_font_size:${mark?.attrs?.pt ?? 'missing'}`);
      }
    }
  }
}

/**
 * @param {unknown} document
 */
export function countLetterTemplateQrBlocks(document) {
  let count = 0;
  walkNode(document, 'doc', (node) => {
    if (node?.type === 'letterQrBlock') count += 1;
  });
  return count;
}

/**
 * @param {unknown} document
 */
export function countLetterTemplateImageNodes(document) {
  let count = 0;
  walkNode(document, 'doc', (node) => {
    if (node?.type === 'letterImage') count += 1;
  });
  return count;
}

/**
 * @param {unknown} document
 * @returns {string[]}
 */
export function extractLetterTemplateAssetIds(document) {
  const ids = [];
  walkNode(document, 'doc', (node) => {
    if (node?.type === 'letterImage' && typeof node.attrs?.assetId === 'string') {
      ids.push(node.attrs.assetId);
    }
  });
  return ids;
}

/**
 * @param {unknown} frenchDocument
 * @param {unknown} englishDocument
 */
export function countCombinedLetterTemplateQrBlocks(frenchDocument, englishDocument) {
  return countLetterTemplateQrBlocks(frenchDocument) + countLetterTemplateQrBlocks(englishDocument);
}

/**
 * @param {unknown} frenchDocument
 * @param {unknown} englishDocument
 */
export function countCombinedLetterTemplateImageNodes(frenchDocument, englishDocument) {
  return countLetterTemplateImageNodes(frenchDocument) + countLetterTemplateImageNodes(englishDocument);
}

/**
 * @param {{
 *   bilingual: boolean,
 *   frenchDocument: object,
 *   englishDocument?: object | null,
 *   translationStale?: boolean,
 * }} params
 */
export function validateLetterTemplateRichDraft({
  bilingual,
  frenchDocument,
  englishDocument,
  translationStale = false,
}) {
  const issues = [];
  const fr = validateLetterTemplateDocument(frenchDocument);
  if (!fr.valid) issues.push(...fr.issues);
  if (bilingual) {
    if (!englishDocument) {
      issues.push('missing_english_document');
    } else {
      const en = validateLetterTemplateDocument(englishDocument);
      if (!en.valid) issues.push(...en.issues);
    }
    const combinedQr = countCombinedLetterTemplateQrBlocks(frenchDocument, englishDocument);
    if (combinedQr > LETTER_TEMPLATE_MAX_QR_BLOCKS) {
      issues.push('too_many_combined_qr');
    }
    const combinedImages = countCombinedLetterTemplateImageNodes(frenchDocument, englishDocument);
    if (combinedImages > LETTER_TEMPLATE_MAX_IMAGE_NODES) {
      issues.push('too_many_combined_images');
    }
  } else {
    if (countLetterTemplateQrBlocks(frenchDocument) > LETTER_TEMPLATE_MAX_QR_BLOCKS) {
      issues.push('too_many_qr_blocks');
    }
    if (countLetterTemplateImageNodes(frenchDocument) > LETTER_TEMPLATE_MAX_IMAGE_NODES) {
      issues.push('too_many_images');
    }
  }
  if (translationStale) {
    issues.push('translation_stale');
  }
  return { valid: issues.length === 0, issues };
}

/**
 * @param {unknown} document
 */
export function createEmptyLetterTemplateDocument() {
  return {
    type: 'doc',
    content: [{ type: 'paragraph' }],
  };
}

/**
 * Documents that native Tiptap getJSON() may emit for an empty paragraph.
 */
export const NATIVE_TIPTAP_EMPTY_PARAGRAPH_JSON = Object.freeze({ type: 'paragraph' });

/**
 * Pure, deterministic normalization for document equality.
 * Strips no-effect Tiptap defaults so a stored canonical document compares
 * equal to the same document after editor getJSON().
 *
 * @param {unknown} document
 */
export function normalizeLetterTemplateDocumentForCompare(document) {
  if (document == null || typeof document !== 'object' || Array.isArray(document)) {
    return document;
  }
  return normalizeLetterTemplateNodeForCompare(document);
}

/**
 * @param {unknown} a
 * @param {unknown} b
 */
export function letterTemplateDocumentsEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  return (
    stableStringifyLetterTemplate(normalizeLetterTemplateDocumentForCompare(a)) ===
    stableStringifyLetterTemplate(normalizeLetterTemplateDocumentForCompare(b))
  );
}

/**
 * Resolve FR document from version detail (rich or legacy).
 * @param {object | null | undefined} version
 */
export function resolveFrenchDocumentFromVersion(version) {
  if (!version) return null;
  if (version.documents?.fr) return version.documents.fr;
  return version.document || null;
}

/**
 * Resolve EN document from version detail.
 * @param {object | null | undefined} version
 */
export function resolveEnglishDocumentFromVersion(version) {
  if (!version) return null;
  return version.documents?.en || null;
}

/**
 * @param {object | null | undefined} version
 */
export function resolveBilingualFromVersion(version) {
  if (!version) return false;
  if (typeof version.bilingual === 'boolean') return version.bilingual;
  return Boolean(version.documents?.en);
}

/**
 * @param {object | null | undefined} template
 * @param {object | null | undefined} version
 */
export function resolveSchemaVersionFromDetail(template, version) {
  if (Number.isInteger(template?.schemaVersion)) return template.schemaVersion;
  if (Number.isInteger(version?.schemaVersion)) return version.schemaVersion;
  if (version?.bilingual || version?.documents?.en) return 2;
  return 1;
}

/**
 * @param {object} node
 * @param {string} parentType
 * @param {(node: object, parentType: string) => void} visit
 */
function walkNode(node, parentType, visit) {
  if (!node || typeof node !== 'object') return;
  visit(node, parentType);
  if (Array.isArray(node.content)) {
    for (const child of node.content) {
      walkNode(child, node.type, visit);
    }
  }
}

/**
 * @param {object} node
 */
function normalizeLetterTemplateNodeForCompare(node) {
  if (!node || typeof node !== 'object' || Array.isArray(node)) return node;
  const out = { type: node.type };
  const attrs = normalizeLetterTemplateAttrsForCompare(node.type, node.attrs);
  if (attrs) out.attrs = attrs;
  if (typeof node.text === 'string') out.text = node.text;
  const marks = normalizeLetterTemplateMarksForCompare(node.marks);
  if (marks) out.marks = marks;
  if (Array.isArray(node.content)) {
    out.content = node.content.map((child) => normalizeLetterTemplateNodeForCompare(child));
  } else if (OPTIONAL_EMPTY_CONTENT_CONTAINER_TYPES.has(node.type)) {
    out.content = [];
  }
  return out;
}

/**
 * @param {string | undefined} type
 * @param {unknown} attrs
 */
function normalizeLetterTemplateAttrsForCompare(type, attrs) {
  if (attrs == null || typeof attrs !== 'object' || Array.isArray(attrs)) return undefined;
  const out = {};
  for (const [key, value] of Object.entries(attrs)) {
    if (key === 'align' && value == null) continue;
    if (type === 'orderedList') {
      if (key === 'start' && value === 1) continue;
      if (key === 'type' && value == null) continue;
    }
    if (type === 'letterQrBlock' && key === 'align' && (value == null || value === 'center')) {
      continue;
    }
    if (type === 'letterImage') {
      if (key === 'alt' && (value == null || value === '')) continue;
      if (key === 'align' && (value == null || value === 'center')) continue;
      if (key === 'widthPct' && value === 50) continue;
    }
    out[key] = value;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * @param {unknown} marks
 */
function normalizeLetterTemplateMarksForCompare(marks) {
  if (!Array.isArray(marks) || marks.length === 0) return undefined;
  return marks.map((mark) => {
    if (!mark || typeof mark !== 'object') return mark;
    const out = { type: mark.type };
    if (mark.attrs && typeof mark.attrs === 'object' && !Array.isArray(mark.attrs)) {
      const attrs = {};
      for (const [key, value] of Object.entries(mark.attrs)) {
        if (value != null) attrs[key] = value;
      }
      if (Object.keys(attrs).length > 0) out.attrs = attrs;
    }
    return out;
  });
}

/**
 * @param {unknown} value
 */
function stableStringifyLetterTemplate(value) {
  if (value === undefined) return 'null';
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringifyLetterTemplate(item)).join(',')}]`;
  }
  const keys = Object.keys(value).sort();
  return `{${keys
    .map((key) => `${JSON.stringify(key)}:${stableStringifyLetterTemplate(value[key])}`)
    .join(',')}}`;
}
